export const meta = {
	slug: 'aizawa-funnel',
	title: 'Aizawa’s round shell with a hole through it',
	tag: 'Attractors',
	excerpt:
		'The Aizawa attractor fills a ball-like shell with a funnel down the middle: six ODE knobs and a cool-to-warm trail on Classical Chaos.',
	dateLabel: 'Aug 2026',
	readMinutes: 5,
	featured: false,
	thumbSlug: 'aizawa_attractor',
	order: 5,
}


Yoji Aizawa’s system is one of those 3D chaos demos that reads as a shape before it reads as equations: a round cloud with a clear tunnel through the axis. Lorenz gets the butterfly. Aizawa gets the punctured ball.

Classical Chaos draws it the same way as the Lorenz stage: Euler steps into a GPU line trail.

<VizEmbed slug="aizawa_attractor" label="Aizawa" animateN />

## The ODEs

Six real parameters: knobs `a` through `f`, plus step size `dt`.

<BlockMath math={'\\dot{x} = (z - b)x - d y'} />
<BlockMath math={'\\dot{y} = d x + (z - b)y'} />
<BlockMath math={'\\dot{z} = c + a z - z^3/3 - (x^2 + y^2)(1 + e z) + f z x^3'} />

Defaults on Classical Chaos: `a=0.95`, `b=0.7`, `c=0.6`, `d=3.5`, `e=0.25`, `f=0.1`, `dt=0.01`, seed `(0.1, 0, 0)`. A second preset keeps longer decimal knobs.

## Why it looks like a funnel

The \(x\) and \(y\) equations rotate and stretch in the horizontal plane as \(z\) changes. The \(z\) equation has a cubic restoring term and a radial factor \((x^2+y^2)\), so trajectories spend time on a thick shell and leave a quieter axis. That empty corridor is the “funnel” you see when the trail builds out.

Change `d` and the swirl tightens or loosens. Nudge `a`/`c` and the vertical thickness shifts. Nothing here is a mesh; it is only the orbit you integrate.

## On the full stage

Trail length grows, scrubs, or holds like Lorenz. Change any of `a`…`f` or `dt` and the trail resets from the seed.

```ts
const nextX = (z - b) * x - d * y
const nextY = d * x + (z - b) * y
const nextZ = c + a * z - (z * z * z) / 3 - (x * x + y * y) * (1 + e * z) + f * z * x * x * x
```

<Callout to="/aizawa_attractor">
	<strong>Goto full canvas</strong>
	Retune `a`–`f` and `dt`, scrub trail length, try a preset, drag to rotate.
</Callout>
